import React, { useContext, useEffect, useState } from 'react';
import {
  AppBar,
  Badge,
  Box,
  Button,
  Divider,
  IconButton,
  ListSubheader,
  Menu,
  MenuItem,
  Toolbar,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import BuildIcon from '@mui/icons-material/Build';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DescriptionIcon from '@mui/icons-material/Description';
import GroupIcon from '@mui/icons-material/Group';
import InventoryIcon from '@mui/icons-material/Inventory';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PriceChangeIcon from '@mui/icons-material/PriceChange';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import HubIcon from '@mui/icons-material/Hub';
import BrandingWatermarkIcon from '@mui/icons-material/BrandingWatermark';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import { AuthContext } from '../context/AuthContext';
import config from '../config';
import StockNotificationsDialog from './StockNotificationsDialog';
import { StockNotification } from '../types/notifications';
import MirakelParticles from './MirakelParticles';

type HeaderEffects = 'playful' | 'subtle';

interface Brand {
  brandName: string;
  unreadCount: number;
}

interface NotificationResponse {
  notifications: StockNotification[];
  pagination: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  };
  counts: {
    totalUnread: number;
    missingDescription: number;
    missingColorImages: number;
    completeAndUnread: number;
    totalBrands: number;
  };
}

interface StockNotificationHandledEvent extends Event {
  detail?: {
    notificationId?: number;
  };
}

interface NavItem {
  label: string;
  path: string;
  icon: React.ReactElement;
  permissions?: string[];
}

interface CustomAppBarProps {
  effects?: HeaderEffects;
}

const productNavGroups: { title: string; items: NavItem[] }[] = [
  {
    title: 'Katalog',
    items: [
      {
        label: 'Hantera Produkter',
        path: '/product-management',
        icon: <DescriptionIcon />,
        permissions: ['manage_product_descriptions'],
      },
      {
        label: 'Produktimport',
        path: '/product-import',
        icon: <CloudUploadIcon />,
        permissions: ['manage_product_descriptions'],
      },
      {
        label: 'Enrichment review',
        path: '/product-management/enrichment',
        icon: <AutoFixHighIcon />,
        permissions: ['manage_product_descriptions'],
      },
    ],
  },
  {
    title: 'Pris & skyltning',
    items: [
      {
        label: 'Prishantering',
        path: '/price-change',
        icon: <PriceChangeIcon />,
        permissions: ['manage_prices'],
      },
      {
        label: 'Prisskyltar',
        path: '/price-tags',
        icon: <PriceChangeIcon />,
        permissions: ['manage_prices'],
      },
    ],
  },
  {
    title: 'Inköp',
    items: [
      {
        label: 'Inköp',
        path: '/purchasing/purchase-orders',
        icon: <ShoppingCartIcon />,
        permissions: ['manage_purchasing'],
      },
      {
        label: 'Inköpsstatistik',
        path: '/purchasing/purchase-orders/statistics',
        icon: <ShowChartIcon />,
        permissions: ['manage_purchasing'],
      },
    ],
  },
  {
    title: 'Analys',
    items: [
      {
        label: 'Försäljning',
        path: '/sales',
        icon: <ShowChartIcon />,
      },
      {
        label: 'Leverantörsstatistik',
        path: '/supplier-stats/dashboard',
        icon: <LocalShippingIcon />,
      },
    ],
  },
];

const adminNavItems: NavItem[] = [
  {
    label: 'Brands och suppliers',
    path: '/brand-suppliers',
    icon: <BrandingWatermarkIcon />,
    permissions: ['manage_companies'],
  },
  {
    label: 'Användarhantering',
    path: '/user-management',
    icon: <GroupIcon />,
    permissions: ['manage_users'],
  },
  {
    label: 'Systemjobb',
    path: '/jobs',
    icon: <BuildIcon />,
    permissions: ['manage_companies'],
  },
  {
    label: 'Google Supplemental',
    path: '/google-supplemental',
    icon: <RssFeedIcon />,
    permissions: ['manage_companies'],
  },
  {
    label: 'Integrationer',
    path: '/integrations',
    icon: <HubIcon />,
    permissions: ['manage_companies'],
  },
];

const CustomAppBar: React.FC<CustomAppBarProps> = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setToken, setPermissions, permissions } = useContext(AuthContext);
  const [anchorElProducts, setAnchorElProducts] = useState<null | HTMLElement>(
    null,
  );
  const [anchorElMobile, setAnchorElMobile] = useState<null | HTMLElement>(
    null,
  );
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [notifications, setNotifications] = useState<StockNotification[]>([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 20,
    totalPages: 1,
  });
  const [filters, setFilters] = useState({
    brand: '',
    hasDescription: '',
    hasAllColorImages: '',
  });
  const [brandsWithCounts, setBrandsWithCounts] = useState<Brand[]>([]);
  const [notificationCounts, setNotificationCounts] = useState({
    totalUnread: 0,
    missingDescription: 0,
    missingColorImages: 0,
    completeAndUnread: 0,
    totalBrands: 0,
  });
  const [isBulkMarking, setIsBulkMarking] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const canShowItem = (item: NavItem) =>
    !item.permissions ||
    item.permissions.every((permission) => permissions.includes(permission));

  const visibleProductItems = productNavGroups
    .flatMap((g) => g.items)
    .filter(canShowItem);
  const visibleAdminItems = adminNavItems.filter(canShowItem);
  const mobileMenuItems = [...visibleProductItems, ...visibleAdminItems];
  const hasVisibleProductItems = visibleProductItems.length > 0;

  const fetchNotifications = async (
    page = 1,
    limit = 20,
    currentFilters = filters,
  ) => {
    try {
      const token = localStorage.getItem('token');
      const queryParams = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        ...(currentFilters.brand && { brand: currentFilters.brand }),
        ...(currentFilters.hasDescription && {
          hasDescription: currentFilters.hasDescription,
        }),
        ...(currentFilters.hasAllColorImages && {
          hasAllColorImages: currentFilters.hasAllColorImages,
        }),
      });

      const response = await fetch(
        `${config.API_URL}/notifications/stock-changes?${queryParams.toString()}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (response.ok) {
        const data: NotificationResponse = await response.json();
        setNotifications(data.notifications);
        setPagination(data.pagination);
        setNotificationCounts(data.counts);
      } else {
        console.error(
          'Fel vid hämtning av notifikationer:',
          await response.text(),
        );
      }
    } catch (error) {
      console.error('Fel vid hämtning av notifikationer:', error);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${config.API_URL}/notifications/stock-changes/${notificationId}/mark-read`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (response.ok) {
        setNotifications(
          notifications.filter((n) => n.notificationId !== notificationId),
        );
        setNotificationCounts((current) => ({
          ...current,
          totalUnread: Math.max(0, current.totalUnread - 1),
        }));
        fetchNotifications(pagination.page, pagination.limit, filters);
      } else {
        console.error(
          'Fel vid markering av notifikation som läst:',
          await response.text(),
        );
      }
    } catch (error) {
      console.error('Fel vid markering av notifikation som läst:', error);
    }
  };

  const handleMarkCompleteAsRead = async () => {
    setIsBulkMarking(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${config.API_URL}/notifications/stock-changes/mark-read-complete`,
        {
          method: 'PATCH',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      if (!response.ok) {
        console.error(
          'Fel vid bulk-markering av notifieringar:',
          await response.text(),
        );
        return;
      }

      await fetchNotifications(1, pagination.limit, filters);
      setPagination((prev) => ({ ...prev, page: 1 }));

      const brandsResponse = await fetch(
        `${config.API_URL}/notifications/brands`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );
      if (brandsResponse.ok) {
        setBrandsWithCounts(await brandsResponse.json());
      }
    } catch (error) {
      console.error('Fel vid bulk-markering av notifieringar:', error);
    } finally {
      setIsBulkMarking(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('permissions');
    setToken(null);
    setPermissions([]);
    navigate('/');
  };

  const handleProductsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElProducts(event.currentTarget);
  };

  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMobile(event.currentTarget);
  };

  const handleProductsMenuClose = () => {
    setAnchorElProducts(null);
  };

  const handleMobileMenuClose = () => {
    setAnchorElMobile(null);
  };

  const handleNavigate = (path: string) => {
    navigate(path);
    handleProductsMenuClose();
    handleMobileMenuClose();
  };

  const handleOpenProductFromNotification = (
    notification: StockNotification,
  ) => {
    setNotificationsOpen(false);
    const params = new URLSearchParams({
      notificationId: String(notification.notificationId),
      returnTo: 'notifications',
    });
    navigate(
      `/product-management/products/${encodeURIComponent(
        notification.productId,
      )}/edit?${params.toString()}`,
    );
  };

  const handlePageChange = (newPage: number) => {
    fetchNotifications(newPage, pagination.limit);
  };

  const handleFilterChange = (filterName: string, value: string) => {
    const newFilters = {
      ...filters,
      [filterName]: value,
    };
    setFilters(newFilters);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  useEffect(() => {
    fetchNotifications(1, 20, filters);
  }, [filters]);

  useEffect(() => {
    fetchNotifications(pagination.page, pagination.limit);
    const interval = setInterval(
      () => fetchNotifications(pagination.page, pagination.limit),
      5 * 60 * 1000,
    );
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (searchParams.get('openStockNotifications') !== '1') {
      return;
    }

    setNotificationsOpen(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('openStockNotifications');
    setSearchParams(nextParams, { replace: true });
  }, [location.pathname, searchParams, setSearchParams]);

  useEffect(() => {
    const handleStockNotificationHandled = (event: Event) => {
      const notificationId = (event as StockNotificationHandledEvent).detail
        ?.notificationId;

      if (typeof notificationId === 'number') {
        setNotifications((current) =>
          current.filter(
            (notification) => notification.notificationId !== notificationId,
          ),
        );
        setNotificationCounts((current) => ({
          ...current,
          totalUnread: Math.max(0, current.totalUnread - 1),
        }));
      }

      fetchNotifications(pagination.page, pagination.limit, filters);
    };

    window.addEventListener(
      'stock-notification:handled',
      handleStockNotificationHandled,
    );

    return () => {
      window.removeEventListener(
        'stock-notification:handled',
        handleStockNotificationHandled,
      );
    };
  }, [filters, pagination.limit, pagination.page]);

  useEffect(() => {
    const fetchBrandsWithCounts = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${config.API_URL}/notifications/brands`, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        });
        if (response.ok) {
          const data = await response.json();
          setBrandsWithCounts(data);
        }
      } catch (error) {
        console.error('Error fetching brands with counts:', error);
      }
    };

    fetchBrandsWithCounts();
  }, []);

  const renderMenuItem = (item: NavItem) => {
    if (!canShowItem(item)) return null;
    return (
      <MenuItem key={item.path} onClick={() => handleNavigate(item.path)}>
        {item.icon}
        {item.label}
      </MenuItem>
    );
  };

  const renderProductMenuContent = () => {
    const visibleGroups = productNavGroups
      .map((group) => ({
        ...group,
        items: group.items.filter(canShowItem),
      }))
      .filter((group) => group.items.length > 0);

    return visibleGroups.flatMap((group, index) => [
      <ListSubheader
        key={`subheader-${group.title}`}
        disableSticky
        sx={{
          lineHeight: 1.2,
          fontSize: '0.72rem',
          fontWeight: 800,
          letterSpacing: 0.6,
          textTransform: 'uppercase',
          color: 'text.secondary',
          py: 1,
          bgcolor: 'transparent',
        }}
      >
        {group.title}
      </ListSubheader>,
      ...group.items.map(renderMenuItem),
      index < visibleGroups.length - 1 ? (
        <Divider key={`divider-${group.title}`} sx={{ my: 0.5 }} />
      ) : null,
    ]);
  };

  return (
    <AppBar
      position="sticky"
      elevation={0}
      sx={{
        top: 0,
        zIndex: (muiTheme) => muiTheme.zIndex.appBar,
      }}
    >
      <Toolbar
        sx={{
          minHeight: { xs: 58, md: 62 },
          px: { xs: 2, md: 3 },
          gap: 1,
          position: 'relative',
          overflow: 'hidden',
          '& > :not(.mirakel-particles)': {
            position: 'relative',
            zIndex: 1,
          },
        }}
      >
        <MirakelParticles
          variant="ambient"
          density="low"
          opacity={0.2}
          sx={{
            display: { xs: 'none', md: 'block' },
            left: 'auto',
            width: 420,
          }}
        />
        <Box
          onClick={() => handleNavigate('/dashboard')}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            mr: { xs: 'auto', md: 2 },
            cursor: 'pointer',
            minWidth: 0,
          }}
        >
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 2,
              display: 'grid',
              placeItems: 'center',
              color: 'common.white',
              bgcolor: 'primary.main',
              boxShadow: `inset 0 -10px 18px ${alpha(
                theme.palette.secondary.main,
                0.45,
              )}`,
              fontWeight: 800,
              fontSize: '0.9rem',
              flexShrink: 0,
            }}
          >
            M
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                color: 'text.primary',
                fontWeight: 800,
                lineHeight: 1,
              }}
            >
              MiRAKEL
            </Typography>
            {!isMobile && (
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  display: 'block',
                  lineHeight: 1.1,
                }}
              >
                Produkt & handel
              </Typography>
            )}
          </Box>
        </Box>

        {!isMobile && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              flexGrow: 1,
              minWidth: 0,
              justifyContent: 'flex-end',
            }}
          >
            {permissions.includes('manage_product_descriptions') && (
              <IconButton
                color="primary"
                onClick={() => setNotificationsOpen(true)}
                aria-label="Öppna notifikationer"
                sx={{
                  mr: 0.5,
                  color: 'text.secondary',
                  '&:hover': {
                    color: 'primary.main',
                    backgroundColor: alpha(theme.palette.primary.main, 0.08),
                  },
                }}
              >
                <Badge
                  badgeContent={notificationCounts.totalUnread}
                  color="error"
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}

            {hasVisibleProductItems && (
              <>
                <Button
                  color="inherit"
                  onClick={handleProductsMenuOpen}
                  startIcon={<InventoryIcon />}
                  sx={{
                    color: 'text.secondary',
                    flexShrink: 0,
                    '&:hover': {
                      color: 'primary.main',
                    },
                  }}
                >
                  Produkter
                </Button>
                <Menu
                  anchorEl={anchorElProducts}
                  open={Boolean(anchorElProducts)}
                  onClose={handleProductsMenuClose}
                  PaperProps={{
                    elevation: 2,
                    sx: { mt: 1, minWidth: 240 },
                  }}
                >
                  {renderProductMenuContent()}
                </Menu>
              </>
            )}

            {visibleAdminItems.map((item) => (
              <Button
                key={item.path}
                color="inherit"
                onClick={() => handleNavigate(item.path)}
                startIcon={item.icon}
                sx={{
                  color: 'text.secondary',
                  flexShrink: 0,
                  '&:hover': {
                    color: 'primary.main',
                  },
                }}
              >
                {item.label}
              </Button>
            ))}

            <Button
              variant="outlined"
              color="primary"
              onClick={handleLogout}
              startIcon={<LogoutIcon />}
              sx={{ ml: 0.75, flexShrink: 0 }}
            >
              Logga ut
            </Button>
          </Box>
        )}

        {isMobile && (
          <>
            {permissions.includes('manage_product_descriptions') && (
              <IconButton
                color="primary"
                onClick={() => setNotificationsOpen(true)}
                aria-label="Öppna notifikationer"
              >
                <Badge
                  badgeContent={notificationCounts.totalUnread}
                  color="error"
                >
                  <NotificationsIcon />
                </Badge>
              </IconButton>
            )}

            <IconButton
              edge="end"
              color="primary"
              aria-label="Öppna navigation"
              onClick={handleMobileMenuOpen}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorElMobile}
              open={Boolean(anchorElMobile)}
              onClose={handleMobileMenuClose}
              PaperProps={{
                elevation: 2,
                sx: { mt: 1, minWidth: 250 },
              }}
            >
              {mobileMenuItems.map(renderMenuItem)}
              <MenuItem onClick={handleLogout}>
                <LogoutIcon />
                Logga ut
              </MenuItem>
            </Menu>
          </>
        )}

        <StockNotificationsDialog
          open={notificationsOpen}
          onClose={() => setNotificationsOpen(false)}
          notifications={notifications}
          onMarkAsRead={handleMarkAsRead}
          onOpenProduct={handleOpenProductFromNotification}
          pagination={pagination}
          onPageChange={handlePageChange}
          filters={filters}
          onFilterChange={handleFilterChange}
          brands={brandsWithCounts}
          counts={notificationCounts}
          onMarkCompleteAsRead={handleMarkCompleteAsRead}
          isBulkMarking={isBulkMarking}
        />
      </Toolbar>
    </AppBar>
  );
};

export default CustomAppBar;
